import React, { useState } from 'react';
import clsx from 'clsx';

import IconArrowUp from '@/assets/icons/icon-arrow-up.svg?react';
import IconArrowDown from '@/assets/icons/icon-arrow-down-02.svg?react';
import IconCopy from '@/assets/icons/icon-copy.svg?react';
import IconMore from '@/assets/icons/icon-more.svg?react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import IconMove from '@/assets/icons/icon-move.svg?react';
import { ROOT_NODE, useEditor, useNode } from '@craftjs/core';
import { isStorybook } from '@/utils/isStorybook';
import { Tooltip } from '@/components/tooltips/Tooltip/Tooltip';
import { DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ListItemDropdown } from '@/components/list-item/ListItemDropdown/ListItemDropdown';
import { DropdownField } from '@/components/dropdowns/DropdownField/DropdownField';
import { useContainerHelper } from '@/hooks/useContainerHelper';
import { useCraftHelper } from '@/hooks/useCraftHelper';
import { canDuplicateNode, isContainerNode, isPopupNode } from '@/utils/helperFunc';
import { useAppDispatch } from '@/store/hooks';
import { setIsOpenSaveSection } from '@/store/section/slice';
import { canDeleteNode } from '@/utils/canDeleteNode';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import type { Breakpoint } from '@builder4/shared/types/layout';
import type { ContainerProps } from '@builder4/shared/components/Container';

interface ElementControlsProps {
  className?: string;
  onTitleEdit: (id: string) => void;
  breakpointCount?: number;
}

export const ElementControls: React.FC<ElementControlsProps> = ({
  className = '',
  onTitleEdit,
  breakpointCount = 0,
}) => {
  const dispatch = useAppDispatch();
  const { duplicateNode, getIndexInSiblings, removeNode, toggleVisibility, toggleLock, copyNode } =
    useCraftHelper();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const editorEnabled = !isStorybook();

  const query = editorEnabled ? useEditor().query : null;
  const actions = editorEnabled ? useEditor().actions : null;
  const { reAdustFillableObjectSpace } = useContainerHelper();
  const {
    id,
    parentId,
    isDeletable,
    connectors,
    nodeName,
    isLocked,
    isRootContainer,
    isPopup,
    canDuplicate,
    nodeProps,
    isDraggable,
  } = editorEnabled
    ? useNode((node) => ({
        parentId: node.data.parent,
        nodeName: node.data.displayName,
        isDraggable: query!.node(node.id).isDraggable(),
        isDeletable: canDeleteNode(query, node.id),
        isLocked: node.data.custom.isLocked,
        isRootContainer: isContainerNode(node) && node.data.parent === ROOT_NODE,
        isPopup: isPopupNode(node),
        canDuplicate: canDuplicateNode(node),
        nodeProps: node.data.props as Partial<ContainerProps>,
      }))
    : {
        id: '',
        parentId: '',
        isDraggable: false,
        connectors: { drag: () => {} },
        nodeName: '',
        isDeletable: false,
        isLocked: false,
        isRootContainer: false,
        isPopup: false,
        canDuplicate: false,
        nodeProps: {} as Partial<ContainerProps>,
      };

  const { getBPProp } = useBreakpointAwareProp(nodeName);
  const position = getBPProp(nodeProps, 'position');
  const moveable = isDraggable && (position === 'relative' || position === 'sticky');

  const handleDuplicate = () => {
    if (!editorEnabled || !query || !actions) return;
    const newNodeId = duplicateNode(id);
    if (!newNodeId) return;
    ['desktop', 'tablet', 'mobile'].forEach((bp) => {
      reAdustFillableObjectSpace(parentId as string, newNodeId, bp as Breakpoint);
    });
  };

  const handleDelete = () => {
    if (!editorEnabled || !query || !actions) return;
    if (isDeletable) {
      removeNode(id);
    }
  };

  const handleMoveUp = () => {
    if (!editorEnabled || !query || !actions) return;
    let newIndex = getIndexInSiblings(id, parentId as string) - 1;
    if (newIndex < 0) {
      newIndex = 0;
    }
    window.__lastDragSource = 'arrows';
    actions.move(id, parentId as string, newIndex);
  };

  const handleMoveDown = () => {
    if (!editorEnabled || !query || !actions) return;
    let newIndex = getIndexInSiblings(id, parentId as string) + 2;
    if (newIndex >= query.node(parentId as string).get().data.nodes.length) {
      newIndex = query.node(parentId as string).get().data.nodes.length;
    }
    window.__lastDragSource = 'arrows';
    actions.move(id, parentId as string, newIndex);
  };

  const toggleNodeVisibility = () => {
    if (!editorEnabled || !query || !actions) return;
    toggleVisibility(id);
  };

  const handleTitleEdit = () => {
    setDropdownOpen(false);
    setTimeout(() => {
      //delay is important to avoind aria-hidden error
      onTitleEdit(id);
    }, 250);
  };

  const handleToggleLock = () => {
    if (!editorEnabled || !query || !actions) return;
    toggleLock(id);
    setDropdownOpen(false);
  };

  const handleCopyNode = () => {
    if (!editorEnabled || !query || !actions) return;
    copyNode(id);
    setDropdownOpen(false);
  };

  const handleOpenSaveSectionModal = () => {
    dispatch(setIsOpenSaveSection(true));
    setDropdownOpen(false);
  };

  return (
    <div
      className={clsx(
        'inline-flex h-7 items-center justify-center gap-2 rounded-md bg-indigo-500 px-1 py-2',
        `${nodeName.toLowerCase()}-node-controls`,
        className,
      )}
    >
      {(isRootContainer || moveable) && breakpointCount < 3 && (
        <>
          <div className="flex items-center gap-0.5">
            {isRootContainer && (
              <>
                <Tooltip
                  textFirst={'Move up'}
                  textSecond={''}
                  className={'flex items-center'}
                  children={
                    <div
                      onClick={handleMoveUp}
                      data-qa-selector="canvas-element-move-up-button"
                      className="hover:bg-neutral-0/20 flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded transition-all"
                    >
                      <IconArrowUp className="h-3.5 w-3.5 text-white" />
                    </div>
                  }
                ></Tooltip>
                <Tooltip
                  textFirst={'Move down'}
                  textSecond={''}
                  className={'flex items-center'}
                  children={
                    <div
                      onClick={handleMoveDown}
                      data-qa-selector="canvas-element-move-down-button"
                      className="hover:bg-neutral-0/20 flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded transition-all"
                    >
                      <IconArrowDown className="h-3.5 w-3.5 text-white" />
                    </div>
                  }
                ></Tooltip>
              </>
            )}

            {moveable && (
              <Tooltip
                textFirst={'Move'}
                textSecond={''}
                className={'flex items-center'}
                children={
                  <div
                    ref={(dom) => {
                      connectors.drag(dom as HTMLElement);
                    }}
                    data-qa-selector="canvas-element-move-button"
                    className="hover:bg-neutral-0/20 flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded transition-all"
                  >
                    <IconMove className="h-3.5 w-3.5 text-white" />
                  </div>
                }
              ></Tooltip>
            )}
          </div>
          <div className="h-3 w-[1px] bg-white/50"></div>
        </>
      )}

      <div className="flex items-center gap-1">
        {breakpointCount < 4 && canDuplicate && (
          <Tooltip
            textFirst={'Duplicate'}
            textSecond={''}
            className={'flex items-center'}
            children={
              <div
                className="hover:bg-neutral-0/20 flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded transition-all"
                onClick={handleDuplicate}
                data-qa-selector="canvas-element-duplicate-button"
              >
                <IconCopy className="h-3.5 w-3.5 text-white" />
              </div>
            }
          ></Tooltip>
        )}
        {breakpointCount < 5 && isDeletable && (
          <Tooltip
            textFirst={'Delete'}
            textSecond={''}
            className={'flex items-center'}
            children={
              <div
                onClick={handleDelete}
                data-qa-selector="canvas-element-delete-button"
                className="hover:bg-neutral-0/20 flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded transition-all"
              >
                <IconDelete className="h-3.5 w-3.5 text-white" />
              </div>
            }
          ></Tooltip>
        )}
        <DropdownField
          open={dropdownOpen}
          childrenMenu={
            <>
              {breakpointCount < 2 && (
                <ListItemDropdown
                  as="div"
                  className="w-full cursor-pointer"
                  handlerClick={handleTitleEdit}
                  shortcut="⌘+R"
                  text="Rename"
                  selection={false}
                  allowSelectionToggle={false}
                />
              )}
              {canDuplicate && (
                <>
                  <ListItemDropdown
                    as="div"
                    className="w-full cursor-pointer"
                    handlerClick={handleCopyNode}
                    shortcut="⌘+C"
                    text="Copy"
                    selection={false}
                    allowSelectionToggle={false}
                  />
                  <ListItemDropdown
                    as="div"
                    className="w-full cursor-pointer"
                    handlerClick={handleDuplicate}
                    shortcut="⌘+D"
                    text="Duplicate"
                    selection={false}
                    allowSelectionToggle={false}
                  />
                </>
              )}

              {(isRootContainer || isPopup) && (
                <ListItemDropdown
                  as="div"
                  className="w-full cursor-pointer"
                  handlerClick={handleOpenSaveSectionModal}
                  shortcut="⌘+S"
                  text="Save Section"
                  selection={false}
                  allowSelectionToggle={false}
                />
              )}

              <div className="py-1">
                <div className="w-full outline outline-offset-[-0.50px] outline-neutral-100"></div>
              </div>

              <ListItemDropdown
                as="div"
                className="w-full cursor-pointer"
                handlerClick={toggleNodeVisibility}
                shortcut="⌘+H"
                text="Hide"
                selection={false}
                allowSelectionToggle={false}
              />

              <ListItemDropdown
                as="div"
                className="w-full cursor-pointer"
                handlerClick={handleToggleLock}
                shortcut="⌘+L"
                text={isLocked ? 'Unlock' : 'Lock'}
                selection={false}
                allowSelectionToggle={false}
              />

              {isDeletable && (
                <ListItemDropdown
                  as="div"
                  className="w-full cursor-pointer"
                  handlerClick={handleDelete}
                  shortcut="⌫"
                  text="Delete"
                  selection={false}
                  allowSelectionToggle={false}
                />
              )}
            </>
          }
          childrenTrigger={
            <>
              <DropdownMenuTrigger className="cursor-pointer outline-none">
                <div
                  data-qa-selector="canvas-element-more-menu-button"
                  className={clsx(
                    'flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded transition-all',
                    dropdownOpen ? 'bg-neutral-0/20' : 'hover:bg-neutral-0/20',
                  )}
                >
                  <IconMore className="h-3.5 w-3.5 text-white" />
                </div>
              </DropdownMenuTrigger>
            </>
          }
          className="max-w-[216px]"
          classNameContent="p-1 w-[140px]"
          size="small"
          text=""
          icon={null}
          onOpenChange={setDropdownOpen}
        />
      </div>
    </div>
  );
};
